import { useMemo } from "react";
import {
    useSdk,
    useServiceContext,
} from "../../contexts/services/hooks/use-services-context";
import type {
    DashboardWidgetSettings,
    EmailDisplayMode,
    Language,
    Settings,
    UserProfile,
} from "../../managers/user";
import { USER_PROFILE_KEY } from "../../managers/user/queries";
import { useSessionQuery, type LiveState } from "../session/useSessionQuery";

export function useUserProfile(): LiveState<UserProfile> {
    return useSessionQuery(USER_PROFILE_KEY, (sdk) =>
        sdk.userManager.getUserProfile(),
    );
}

export type UserSettings = {
    getLanguages: () => Language[];
    setPreferredLanguage: (language: Language) => Promise<boolean>;
    setUserEmailDisplayMode: (mode: EmailDisplayMode) => Promise<boolean>;
    /** Fusionne `widgets` dans les widgets du profil. */
    setDashboardWidgets: (widgets: DashboardWidgetSettings) => Promise<boolean>;
    setIsOnboardingDone: (isOnboardingDone: boolean) => Promise<unknown>;
};

/**
 * Les réglages s'appliquent au profil partagé avant la réponse du backend,
 * et y sont annulés si le backend les refuse ou si l'appel échoue.
 */
export function useUserSettings(): UserSettings {
    const sdk = useSdk();

    return useMemo(() => {
        const { userManager, queryClient } = sdk;

        const updateSettings = (update: (settings: Settings) => Settings) =>
            queryClient.setQueryData<UserProfile>(
                USER_PROFILE_KEY,
                (profile) =>
                    profile && {
                        ...profile,
                        settings: update(profile.settings),
                    },
            );

        const applyOptimistically = async (
            patch: (settings: Settings) => Settings,
            save: () => Promise<boolean>,
        ): Promise<boolean> => {
            const previous =
                queryClient.getQueryData<UserProfile>(
                    USER_PROFILE_KEY,
                )?.settings;
            updateSettings(patch);
            const rollback = () => {
                if (previous) updateSettings(() => previous);
            };

            try {
                const saved = await save();
                if (!saved) rollback();
                return saved;
            } catch (error) {
                rollback();
                throw error;
            }
        };

        return {
            getLanguages: () => userManager.getLanguages(),
            setPreferredLanguage: (language) =>
                applyOptimistically(
                    (settings) => ({
                        ...settings,
                        preferredLanguage: language,
                    }),
                    () => userManager.setPreferredLanguage(language),
                ),
            setUserEmailDisplayMode: (mode) =>
                applyOptimistically(
                    (settings) => ({
                        ...settings,
                        displayPreferences: {
                            ...settings.displayPreferences,
                            emailDisplayMode: mode,
                        },
                    }),
                    () => userManager.setUserEmailDisplayMode(mode),
                ),
            setDashboardWidgets: (widgets) =>
                applyOptimistically(
                    (settings) => ({
                        ...settings,
                        displayPreferences: {
                            ...settings.displayPreferences,
                            dashboardWidgets: {
                                ...settings.displayPreferences.dashboardWidgets,
                                ...widgets,
                            },
                        },
                    }),
                    () => userManager.setDashboardWidgets(widgets),
                ),
            setIsOnboardingDone: (isOnboardingDone) =>
                userManager.setIsOnboardingDone(isOnboardingDone),
        };
    }, [sdk]);
}

export type UserPhotos = {
    getUserPhotoByEmail: (email: string) => Promise<string>;
};

/** Utilisable avant la création de la session : la lecture échoue alors. */
export function useUserPhotos(): UserPhotos {
    const userManager = useServiceContext().sdk?.userManager;

    return useMemo(
        () => ({
            getUserPhotoByEmail: async (email) => {
                if (!userManager) {
                    throw new Error("SDK session is not available");
                }
                return userManager.getUserPhotoByEmail(email);
            },
        }),
        [userManager],
    );
}
